import { ICommonRes } from '../common';
import { ICloudService } from '../subscriptions';
import { IDeviceFactoryData, IDeviceFeatures } from './features';

export enum EAccountDeviceStatus {
    BOUND = 'bound',
    UNBOUND = 'unbound',
    SAVED = 'saved',
    SHARED = 'shared'
}

export interface IAccountDevice {

    /**
     * 设备拥有者账户ID
     * @type uint53(snowflake-si-id)
     *
     */
    'owner': string;

    /**
     * 设备的 UID
     *
     * @type string(16)
     */
    'uid': string;

    /**
     * 绑定状态。
     *
     * 取值如下
     *
     * - bound 当前账号绑定中
     * - unbound 当前账号未绑定
     * - saved 仅保存 UID 在当前账户（旧设备）
     * - shared 其他人分享给当前账号的设备
     */
    'status': EAccountDeviceStatus;

    /**
     * 绑定时间。
     *
     * @type uint53(ms-timestamp)
     */
    'boundAt': number;

    /**
     * 产品信息相关
     * 根据请求body中的includes是否包含model确定是否返回
     * 绑定、解绑和保存的设备均可以返回有效值
     */
    'model'?: {

        /**
         * 型号对外名称。
         *
         * @type string(1,64)
         */
        'title': string;
    };

    /**
     * 由板端上传的设备实时状态信息
     * 根据请求body中的includes是否包含channels确定是否返回
     * 只有绑定的设备才返回有效值
     */
    'channels'?: Array<{
        /**
         * 通道号。
         *
         * > 如果有多通道（NVR、排插、双目），则使用 -1 表示主机本身。
         * > 否则（IPC、插座）使用 0 表示机器本身。
         *
         * @type int(-1,255)
         */
        'channelId': number;

        /**
         * 该通道的显示名称（OSD）。
         *
         * @type string(0,64)
         */
        'title'?: string;

        /**
         * 设备状态信息,具体的值由客户端和板端工程师共同确定
         */
        'state'?: Record<string, unknown>;
    }>;

    /**
     * 由板端上传的功能信息
     * 根据请求body中的includes是否包含hwFeatures确定是否返回
     * 只有绑定的设备才返回有效值
     */
    'hwFeatures'?: Omit<IDeviceFeatures, 'modifiedAt'>;

    /**
     * 由板端上传的配置模板信息
     * 根据请求body中的includes是否包含configTemplate确定是否返回
     * 只有绑定的设备才返回有效值
     */
    'configTemplate'?: Record<string, unknown>;

    /**
     * 由客户端设置的产品的在线配置信息,具体的值由客户端和板端工程师共同确定
     * 根据请求body中的includes是否包含onlineConfig确定是否返回
     * 只有绑定的设备才返回有效值
     */
    'onlineConfig'?: {
        /**
         * 配置信息
         */
        'config': Record<string, unknown>;

        /**
         * 设备的配置状态
         * default   设备使用默认信息
         * current   设备使用当前配置
         */
        'status': 'default' | 'current';

        /**
         * 设备的配置版本
         * @types string(1,128)
         */
        'edition': string;

        /**
         * 最后修改时间。
         *
         * @type uint53(ms-timestamp)
         */
        'modifiedAt': number;
    };

    /**
     * 工厂生产生成的uid非隐私部分的相关信息
     * 根据请求body中的includes是否包含factoryData确定是否返回
     * 绑定、解绑和保存的设备均可以返回有效值
     */
    'factoryData'?: IDeviceFactoryData;

    /**
     * 免密登录相关的协议、算法等
     * 根据请求body中的includes是否包含passwordless确定是否返回
     * 只有绑定的设备才返回有效值
     */
    'passwordless'?: {

        /**
         * 当前是否开启。
         */
        'enabled': boolean;

        /**
         * 支持的协议版本号。
         *
         * @type uint32[]
         */
        'protocols': number[];

        /**
         * 设备的认证公钥算法。
         *
         * > 如果设备不支持 v3 协议，则该字段为 `none`。
         */
        'pubKeyAlgo': 'rsa' | 'none' | 'ecc';
    };

    /**
     * 板端的在线信息(用来判断是否长期离线）
     * 根据请求body中的includes是否包含online确定是否返回
     * 只有绑定的设备才返回有效值
     */
    'online'?: {

        /**
         * 设备最后一次请求时间
         *  毫秒时间戳
         */
        'lastRequestAt': number;

        /**
         * 设备的在线状态
         * active - 正常在线
         * inactive - 长期离线
         */
        'status': 'active' | 'inactive';
    };

    /**
     * 设备的云服务id 列表
     * 根据请求 body 中的 includes 是否包含 cloudServices 确定是否返回
     */
    'cloudServices'?: string[];

    /**
     * 最近的录像统计
     */
    'recentVideos'?: {

        /**
         * 数量
         */
        'qty': number;

        /**
         * 统计的时长，毫秒时间戳
         */
        'duration': number;
    };

    /**
     * 最近的图片统计
     */
    'recentImages'?: {

        /**
         * 数量
         */
        'qty': number;

        /**
         * 统计的时长，毫秒时间戳
         */
        'duration': number;
    };

    /**
     * 最近的流量统计
     */
    'dataUsage'?: {

        /**
         * 字节
         */
        'bytes': number;

        /**
         * 统计的时长，毫秒时间戳
         */
        'duration': number;
    };

    /**
     * simCard信息
     * 取值：
     * - null 表示无关联simCard
     */
    'simCard'?: {
        /**
         * 卡号
         * 取值:
         * - unknown 非我司simCard
         */
        'iccid': string;

        /**
         * 绑定状态。
         *
         * 取值如下
         *
         * - bound 当前SIM卡绑定了账号
         * - unbound 当前SIM卡未绑定账号
         */
        'status': 'bound' | 'unbound' | 'bound_other';

        /**
         * SIM卡状态
         * 取值范围：
         * - inventory
         * - active
         * - test_ready
         * - suspended
         * - retired(废弃，无法使用)
         * - unknown(不是我们的卡)
         */
        'state': ESIMCardStatus;
    } | null;

}

export enum EDeviceInfoModule {
    MODEL = 'model',
    HW_FEATURES = 'hwFeatures',
    FACTORY_DATA = 'factoryData',
    ONLINE_CONFIG = 'onlineConfig',
    CONFIG_TEMPLATE = 'configTemplate',
    CHANNELS = 'channels',
    PASSWORDLESS = 'passwordless',
    ONLINE = 'online',
    CLOUDSERVICES = 'cloudServices',
    RECENT_IMAGE = 'recentImages',
    RECENT_VIDEO = 'recentVideos',
    DATA_USAGE = 'dataUsage',
    SIMCARD = 'simCard'
}

export interface IGetAccountDeviceListArgs {

    /**
     * 设备绑定状态
     */
    'statuses': EAccountDeviceStatus[];

    /**
     * 返回指定uid的信息，默认返回所有设备的信息
     * 是个数组
     * @type string(16)[1,64]  每次最多查询 64 个设备的信息。
     */
    uids?: string[];

    /**
     * 返回的指定返回的数据模块
     * 可选的有:
     * model
     * hwFeatures
     * factoryData
     * onlineConfig
     * configTemplate
     * channels
     * passwordless
     * online
     * cloudServices
     * simCard
     */
    includes: Array<Exclude<EDeviceInfoModule, EDeviceInfoModule.RECENT_IMAGE | EDeviceInfoModule.RECENT_VIDEO | EDeviceInfoModule.DATA_USAGE>>;

    /**
     * 语言 key，需要多语言文案的时候传递
     *
     * 默认使用 en
     */
    lang?: string;

}

export interface IGetAccountDeviceArgs {

    /**
     * 账户id
     * 为了兼容分享的情况，不一定查的都是自己的设备，但是目前只限制能查自己的设备
     */
    'owner': string;

    /**
     * 设备16字节的uid
     * @type string(16)
     */
    'uid': string;

    /**
     * 返回的指定返回的数据模块
     * 可选的有:
     * model
     * hwFeatures
     * factoryData
     * onlineConfig
     * configTemplate
     * channels
     * passwordless
     * online
     * recentVideos
     * recentImages
     * dataUsage
     * simCard
     */
    'includes': EDeviceInfoModule[];

    /**
     * 语言 key，需要多语言文案的时候传递
     *
     * 默认使用 en
     */
    'lang'?: string;

}

export type TGetAccountDeviceItemRes = IAccountDevice & {
    /**
     * 设备的云服务列表
     * 根据请求 body 中的 includes 是否包含 cloudServices 确定是否返回
     *
     */
    'services'?: Record<string /* 云服务 id */, ICloudService>;
};

export interface IGetAccountDeviceRes extends ICommonRes<IAccountDevice> {

    /**
     * 设备的云服务列表
     * 根据请求 body 中的 includes 是否包含 cloudServices 确定是否返回
     */
    cloudServices?: Record<string, ICloudService>;

}

/**
 * SIM卡状态
 */
export enum ESIMCardStatus {
    INVENTORY = 'inventory',
    ACTIVE = 'active',
    TEST_READY = 'test_ready',
    SUSPENDED = 'suspended',
    /** 废弃，无法使用 */
    RETIRED = 'retired',
    RETIRING = 'retiring',
    /** 不是我们的卡 */
    UNKNOWN = 'unknown'
}

export interface ISetDeviceAiFeaturesArgs {
    /**
     * 需要修改配置的设备信息列表
     *
     */
    devices: Array<{

        /**
         * 设备 uid
         *
         * 取值范围： 16 个字符
         */
        uid: string;

        /**
         * 设置目标设备AI智能搜索功能的开启与关闭
         */
        smartAiSearchEnabled?: boolean;

        /**
         * 设置目标设备AI智能摘要功能的开启与关闭
         */
        smartAiCaptionEnabled?: boolean;

        /**
         * 设置目标设备AI智能总结功能的开启与关闭
         */
        smartAiSummaryEnabled?: boolean;
    }>;
}

export interface IDeviceProfile {
    color: string | null;
    model: string;
}
